import React, { useState, useMemo, useEffect, useContext } from 'react';
import { Col, Container, Row, Button } from 'reactstrap';
import { useGlobalStudy } from 'context/StudyContext';
import CommonSearch from 'Components/Common/CommonSearch';
import CommonTableIndexWise from '../../../../Components/Common/CommonTableIndexWise';
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from 'common/constants/PermissionConstants';
import { formatDate, GetUserData } from 'common/service/CommonService';
import StatCard from '../../../../Components/Common/StatCard';

import XLSX from 'xlsx-js-style';
import { useReportsSummaryService } from 'modules/Pharmacy/Reports/api/useReportsSummary';
import CommonButton from 'Components/Common/CommonButton';
import { BreadcrumbContext } from 'Layouts/Header';
import { REPORTS_PATHS } from 'Routes/paths';

const StockSummaryReport = () => {
    const breadcrumbContext = useContext(BreadcrumbContext);

    useEffect(() => {
        breadcrumbContext?.setBreadcrumbItems?.([
            { text: "Pharmacy Reports", link: REPORTS_PATHS.SUMMARY },
            { text: "IP Stock Summary" }
        ]);
        return () => breadcrumbContext?.setBreadcrumbItems?.([]);
    }, []);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(10);
    const [searchText, setSearchText] = useState<string>("");

    const { selectedStudy: globalStudy } = useGlobalStudy();
    const [selectedStudy, setSelectedStudy] = useState<string>("");

    useEffect(() => {
        if (globalStudy?.studyid) {
            setSelectedStudy(String(globalStudy.studyid));
        }
        else if (globalStudy === null) {
            console.log("selected studyId");
            setSelectedStudy("");
        }
    }, [globalStudy]);
    const { SelectIpReportsQuery } = useReportsSummaryService();

    const activeStudyId = selectedStudy || globalStudy?.studyid || null;
    const ipSelectQuery = SelectIpReportsQuery({
        studyid: activeStudyId ? Number(activeStudyId) : null,
        pageNumber: currentPage,
        pageSize: itemsPerPage,
        searchText: searchText
    });
    const inventoryList = ipSelectQuery.data?.data || [];
    const totalRecords = inventoryList.length > 0 ? inventoryList[0].total_count : 0;

    // Dynamic stats computation from response payload
    const stats = useMemo(() => {
        if (inventoryList.length === 0) {
            return {
                totalItems: 0,
                lowStock: 0,
                expiringSoon: 0,
            };
        }
        return {
            totalItems: inventoryList[0].totalitems || 0,
            lowStock: inventoryList[0].lowstock || 0, // Not provided directly in summary API, kept for UI structure
            expiringSoon: inventoryList[0].expiringwithin30days || 0
        };
    }, [inventoryList]);

    const columns: any = [
        {
            key: 'studycode',
            label: 'Study Code',
            render: (val: any) => <span className="text-dark fw-semibold fs-13">{val || '-'}</span>
        },
        {
            key: 'ipcategory',
            label: 'IP Category',
            render: (val: any) => {
                const isTestProduct =
                    val?.toLowerCase() === 'test product';

                return (
                    <span
                        className="px-2 py-1 fs-11 rounded fw-semibold"
                        style={{
                            background: isTestProduct
                                ? 'rgba(124, 58, 237, 0.10)'
                                : 'rgba(15, 118, 110, 0.10)',

                            color: isTestProduct
                                ? '#7c3aed'
                                : '#0f766e',

                            border: isTestProduct
                                ? '1px solid rgba(124, 58, 237, 0.18)'
                                : '1px solid rgba(15, 118, 110, 0.18)',

                            letterSpacing: '0.2px'
                        }}
                    >
                        {val || '-'}
                    </span>
                );
            }
        },
        {
            key: 'productname',
            label: 'Product Name',
            render: (val: any) =>
                <span className="text-dark fw-bold fs-13">{val || '-'}</span>
        },
        {
            key: 'batchlotno',
            label: 'Batch / Lot No',
            render: (val: any) => <span className="badge bg-light text-muted border px-2 py-1 fs-12">{val || '-'}</span>
        },
        {
            key: 'manufacturername',
            label: 'Manufacturer Name',
            render: (val: any) => <span className="text-muted fs-13">{val || '-'}</span>
        },
        {
            key: 'manufacturingdate',
            label: 'Manufacturing Date',
            render: (val: any) => <span className="text-muted fs-13">{formatDate(val)}</span>
        },
        {
            key: 'expirydate',
            label: 'Expiry Date',
            render: (val: any) => <span className="text-muted fs-13 fw-medium">{formatDate(val)}</span>
        },
        {
            key: 'currentquantity',
            label: 'Current Stock',
            render: (val: any) => {
                const qty = Number(val) || 0;
                const statusColor = qty <= 10 ? 'danger' : 'success';
                return (
                    <span className={`text-${statusColor} fw-bold d-flex align-items-center gap-1 fs-13`}>
                        <i className={`ri-checkbox-blank-circle-fill fs-10 text-${statusColor}`}></i> {val || 0}
                    </span>
                );
            }
        }
    ];

    const onSearch = (text: string | null) => {
        setSearchText(text ?? "");
        setCurrentPage(1);
    };

    const handleExport = () => {
        const fileName = `IP_Stock_Summary_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;

        const headerRow = [
            "Study Code",
            "IP Category",
            "Product Name",
            "Batch / Lot No",
            "Manufacturer Name",
            "Manufacturing Date",
            "Expiry Date",
            "Current Stock"
        ];
        const dataRows = inventoryList.map((item: any) => [
            item.studycode || "-",
            item.ipcategory || "-",
            item.productname || "-",
            item.batchlotno || "-",
            item.manufacturername || "-",
            formatDate(item.manufacturingdate) || "-",
            formatDate(item.expirydate) || "-",
            Number(item.currentquantity) ?? 0
        ]);

        const user = GetUserData();

        const titleRow = ["IP STOCK SUMMARY REPORT", "", "", "", "", "", "📦", ""];
        const subtitle = ["Overview of current stock levels across all locations", "", "", "", "", "", "", ""];
        const generatedRow = [`Generated On: ${new Date().toLocaleString()} | Generated By: ${user?.userdisplayname || 'System User'}`, "", "", "", "", "", "", ""];

        const allRows = [
            [""], // Row 1: Spacer
            titleRow, // Row 2
            subtitle, // Row 3
            generatedRow, // Row 4
            [""], // Row 5: Spacer between generated metadata and table headers
            headerRow, // Row 6
            ...dataRows,
            [""], // Spacer Row before warning note
            [`🛡️ Note: Figures are system generated. Please verify before taking action.`, "", "", "", "", "", "", ""] // Footer warning row
        ];

        const ws = XLSX.utils.aoa_to_sheet(allRows);

        // Core Premium Palette Styles (Segoe UI Font Family)
        const titleStyle = {
            font: { name: "Segoe UI", sz: 18, bold: true, color: { rgb: "064E3B" } }, // Dark green
            alignment: { vertical: "center", horizontal: "center" },
            fill: { fgColor: { rgb: "FFFFFF" } }
        };

        const subtitleStyle = {
            font: { name: "Segoe UI", sz: 10, italic: true, color: { rgb: "047857" } }, // Medium green
            alignment: { vertical: "center", horizontal: "center" },
            fill: { fgColor: { rgb: "FFFFFF" } }
        };

        const metaStyle = {
            font: { name: "Segoe UI", sz: 9.5, color: { rgb: "4B5563" } }, // Cool gray
            alignment: { vertical: "center", horizontal: "center" }, // Center aligned!
            fill: { fgColor: { rgb: "FFFFFF" } }
        };

        const iconStyle = {
            font: { name: "Segoe UI Emoji", sz: 32 }, // Segoe UI Emoji renders package 📦 beautifully
            alignment: { vertical: "center", horizontal: "center" },
            fill: { fgColor: { rgb: "EBF7EE" } } // Premium mint-green card background
        };

        const headerStyle = {
            font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "064E3B" } }, // Deep forest green header
            alignment: { vertical: "center", horizontal: "center" },
            border: {
                top: { style: "thin", color: { rgb: "043E2B" } },
                bottom: { style: "thin", color: { rgb: "043E2B" } },
                left: { style: "thin", color: { rgb: "043E2B" } },
                right: { style: "thin", color: { rgb: "043E2B" } }
            }
        };

        const cellBorder = {
            top: { style: "thin", color: { rgb: "E2E8F0" } },
            bottom: { style: "thin", color: { rgb: "E2E8F0" } },
            left: { style: "thin", color: { rgb: "E2E8F0" } },
            right: { style: "thin", color: { rgb: "E2E8F0" } }
        };

        const textStyle = {
            font: { name: "Segoe UI", sz: 10, color: { rgb: "1F2937" } },
            alignment: { vertical: "center", horizontal: "center" },
            border: cellBorder
        };

        const dateStyle = {
            font: { name: "Segoe UI", sz: 10, color: { rgb: "1F2937" } },
            alignment: { vertical: "center", horizontal: "center" },
            border: cellBorder
        };

        const footerStyle = {
            font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: "064E3B" } }, // Dark green
            alignment: { vertical: "center", horizontal: "center" },
            fill: { fgColor: { rgb: "EBF7EE" } } // Super light mint green
        };

        const getIpCategoryStyle = (val: string, rowFill: any) => {
            const isTestProduct = val?.toLowerCase() === 'test product';
            return {
                font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: isTestProduct ? "7C3AED" : "0F766E" } },
                fill: rowFill,
                alignment: { vertical: "center", horizontal: "center" },
                border: cellBorder
            };
        };

        // Custom Merge Rules
        ws["!merges"] = [
            // Title (A2:F2)
            { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
            // Subtitle (A3:F3)
            { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } },
            // Meta info (A4:F4)
            { s: { r: 3, c: 0 }, e: { r: 3, c: 5 } },
            // Icon (G2:H4)
            { s: { r: 1, c: 6 }, e: { r: 3, c: 7 } },
            // Warning note at the end (A_last:H_last)
            { s: { r: 6 + dataRows.length + 1, c: 0 }, e: { r: 6 + dataRows.length + 1, c: 7 } }
        ];

        const getIconBorder = (r: number, c: number) => {
            return {
                top: r === 1 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined,
                bottom: r === 3 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined,
                left: c === 6 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined,
                right: c === 7 ? { style: "thin", color: { rgb: "A5D6A7" } } : undefined
            };
        };

        const getFooterBorder = (c: number) => {
            const border: any = {
                top: { style: "thin", color: { rgb: "A5D6A7" } },
                bottom: { style: "thin", color: { rgb: "A5D6A7" } }
            };
            if (c === 0) border.left = { style: "thin", color: { rgb: "A5D6A7" } };
            if (c === 7) border.right = { style: "thin", color: { rgb: "A5D6A7" } };
            return border;
        };

        const range = XLSX.utils.decode_range(ws['!ref'] || "");
        for (let r = range.s.r; r <= range.e.r; ++r) {
            const isAlternate = r % 2 === 0;
            const rowFill = isAlternate ? { fgColor: { rgb: "F5F8F6" } } : { fgColor: { rgb: "FFFFFF" } };

            for (let c = range.s.c; c <= range.e.c; ++c) {
                const cellRef = XLSX.utils.encode_cell({ r, c });
                if (!ws[cellRef]) continue;

                if (r === 0) {
                    ws[cellRef].s = {
                        fill: { fgColor: { rgb: "FFFFFF" } }
                    };
                } else if (r === 1) {
                    if (c <= 5) {
                        ws[cellRef].s = titleStyle;
                    } else {
                        ws[cellRef].s = { ...iconStyle, border: getIconBorder(r, c) };
                    }
                } else if (r === 2) {
                    if (c <= 5) {
                        ws[cellRef].s = subtitleStyle;
                    } else {
                        ws[cellRef].s = { ...iconStyle, border: getIconBorder(r, c) };
                    }
                } else if (r === 3) {
                    if (c <= 5) {
                        ws[cellRef].s = metaStyle;
                    } else {
                        ws[cellRef].s = { ...iconStyle, border: getIconBorder(r, c) };
                    }
                } else if (r === 4) {
                    // Spacer row
                    ws[cellRef].s = {
                        fill: { fgColor: { rgb: "FFFFFF" } }
                    };
                } else if (r === 5) {
                    ws[cellRef].s = headerStyle;
                } else if (r > 5 && r < 6 + dataRows.length) {
                    // Data Rows
                    if (c === 1) {
                        // IP Category - colored text only
                        const ipVal = ws[cellRef].v?.toString() || '';
                        ws[cellRef].s = getIpCategoryStyle(ipVal, rowFill);
                    } else if (c === 5 || c === 6) {
                        ws[cellRef].s = {
                            ...dateStyle,
                            fill: rowFill
                        };
                    } else if (c === 7) {
                        // Color number stock using palette
                        const valNum = Number(ws[cellRef].v) || 0;
                        let textCol = "1F2937"; // Default Text
                        if (valNum <= 10) {
                            textCol = "DC2626"; // Danger
                        } else if (valNum <= 100) {
                            textCol = "D97706"; // Warning
                        } else {
                            textCol = "059669"; // Success
                        }

                        ws[cellRef].s = {
                            font: { name: "Segoe UI", sz: 10, bold: true, color: { rgb: textCol } },
                            alignment: { vertical: "center", horizontal: "center" },
                            border: cellBorder,
                            fill: rowFill
                        };
                    } else {
                        ws[cellRef].s = {
                            ...textStyle,
                            fill: rowFill
                        };
                    }
                } else if (r === 6 + dataRows.length) {
                    // Spacer before footer
                    ws[cellRef].s = {
                        fill: { fgColor: { rgb: "FFFFFF" } }
                    };
                } else if (r === 6 + dataRows.length + 1) {
                    // Footer warning row
                    ws[cellRef].s = {
                        ...footerStyle,
                        border: getFooterBorder(c)
                    };
                }
            }
        }

        // Apply column widths
        ws['!cols'] = [
            { wch: 14 },
            { wch: 16 },
            { wch: 18 },
            { wch: 15 },
            { wch: 18 },
            { wch: 20 },
            { wch: 15 },
            { wch: 15 }
        ];

        // Row Heights
        ws['!rows'] = [
            { hpt: 20 }, // Spacer Row 1
            { hpt: 30 }, // Title Row 2
            { hpt: 22 }, // Subtitle Row 3
            { hpt: 22 }, // Generated Row 4
            { hpt: 20 }, // Spacer Row 5
            { hpt: 26 }  // Header Row 6
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "IP Stock Summary");
        XLSX.writeFile(wb, fileName);
    };

    return (
        <div className="page-content">
            <Container fluid className="px-4">
                <div className="bg-white p-4 rounded shadow-sm mb-4 border border-light">
                    {/* Summary Cards */}
                    <Row className="mb-4 g-3 px-1">
                        <Col lg={4} md={12}>
                            <StatCard
                                title="TOTAL ITEMS"
                                value={stats.totalItems}
                                subtext="across all study batches"
                                icon={<i className="ri-stack-line" />}
                                bgColor="#eff6ff"
                                iconColor="#2563eb"
                                trendType="info"
                                subtextIcon="ri-file-list-3-line"
                            />
                        </Col>
                        <Col lg={4} md={12}>
                            <StatCard
                                title="LOW STOCK"
                                value={stats.lowStock}
                                subtext="items under safe threshold"
                                icon={<i className="ri-error-warning-line" />}
                                bgColor="#fffbeb"
                                iconColor="#d97706"
                                trendType="negative"
                                subtextIcon="ri-error-warning-line"
                            />
                        </Col>
                        <Col lg={4} md={12}>
                            <StatCard
                                title="EXPIRING SOON"
                                value={stats.expiringSoon}
                                subtext="within next 30 days"
                                icon={<i className="ri-alarm-warning-line" />}
                                bgColor="#fef2f2"
                                iconColor="#dc2626"
                                trendType="negative"
                                subtextIcon="ri-time-line"
                            />
                        </Col>
                    </Row>

                    {/* Table Section Header */}
                    <div className="d-flex justify-content-between align-items-center mb-4 mt-5">
                        <div>
                            <h5 className="mb-1 text-dark fw-bold fs-16">IP Stock Summary</h5>
                            <p className="text-muted mb-0 fs-13">Showing {totalRecords} records</p>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <CommonSearch onSearch={onSearch} searchText={searchText} />
                            <Button
                                color="light"
                                className="d-flex align-items-center gap-2 text-dark fs-13 border border-light-subtle px-3 py-2"
                                style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}
                                onClick={handleExport}
                            >
                                <i className="ri-file-excel-2-line text-success fs-15"></i>
                                Export Excel
                            </Button>
                        </div>
                    </div>

                    {/* Common Table */}
                    <div className="border rounded border-light overflow-hidden">
                        <CommonTableIndexWise
                            moduleName={PERMISSION_MODULES.DISPENSING_LOG}
                            columns={columns}
                            data={inventoryList}
                            loading={ipSelectQuery.isLoading || ipSelectQuery.isFetching}
                            pageSize={itemsPerPage}
                            currentPage={currentPage}
                            totalRecords={totalRecords}
                            onPageChange={setCurrentPage}
                            hideAction={true}
                        />
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default StockSummaryReport;
